import { parkingRules, rangesHit } from './parking-rules'

// Warning or just a note (Liam, 26 Oct 2026: Sid Carter's job note "Anthony Carters Dad" put a red "!" on him, "not really
// sure why", then "this should be a ordinary note is there a place for that to be triaged?" and the combined design A + B,
// "I like it ship it"; https://claude.ai/artifact/DQDTZVjYNeD6iygXYG47XZ). Sonnet and GPT-6.1 listed the edge cases first.
// Each of a customer's four notes is a Warning (Today's red "!") or Just a note (a grey note picture): Roundly guesses by
// the words, the owner's choice on their page wins and sticks until the note's words change (customers.note_kinds, 0088,
// keyed by the words it was made for). Parking is a Warning only on a visit inside its hours, or with no time to check.
// The address's own capitals warning is always one; a visit's note and From last time are guessed, with no choice.

export type NoteKind = 'warn' | 'note'
export type ChosenField = 'job_notes' | 'parking_restrictions' | 'preferred_times' | 'call_notes'
export const CHOSEN_FIELDS: ChosenField[] = ['job_notes', 'parking_restrictions', 'preferred_times', 'call_notes']
export type Choices = Partial<Record<ChosenField, { kind: NoteKind; for: string }>>

// The words a choice was made for, short: the same note written the same way keeps its choice; any change guesses again.
export function wordsKey(text: string): string {
  let h = 0x811c9dc5
  // Prices taken out first, as a helper's phone with prices hidden sees the note ("£60" reads "£…"), so the owner's choice
  // reaches them too (Sonnet's notes review 4).
  const words = text.replace(/([£$])\s?(?:\d{1,3}(?:,\d{3})+|\d+|…)(?:\.\d{1,2})?/g, '$1…').trim().replace(/\s+/g, ' ')
  for (const ch of words) { h ^= ch.codePointAt(0)!; h = Math.imul(h, 0x01000193) >>> 0 }
  return h.toString(16).padStart(8, '0')
}

export function choicesOf(json: string | null | undefined): Choices {
  if (!json) return {}
  try { const o = JSON.parse(json); return o && typeof o === 'object' && !Array.isArray(o) ? o as Choices : {} } catch { return {} }
}

// Lines Roundly writes itself into a visit's note ("running late", Not done from Owed): never a warning (Sonnet 10). A voice
// note's line keeps its spoken words, its "🎙 12:03 —" taken off (Sonnet's notes review 2). Only for a visit's note and From
// last time: a customer's own note starting "9:00 - key under the mat" is read as written (review 3).
const SYSTEM_LINE = /^\s*(?:⏱|Not done: marked from Owed)/u
const VOICE_PREFIX = /^\s*🎙\s*(?:\d{1,2}:\d{2}\s*[—–-]\s*)?/u
// A warning in its words. Whole words; "Mr Gates", "keyboard" and "Anthony Carters" are not (Sonnet 2).
const WARN = /\b(dogs?|puppy|bites?|aggressive|beware|danger(?:ous)?|careful|fragile|alarm(?:ed|s)?|key ?safe|key ?holders?|keys?|padlock(?:ed)?|locked|no access|gate code|door code|code\s*:?\s*\d{3,}|asbestos|wasps?|hornets?|bees|nest|ladders?|permit(?: holders?)?(?: only)?|residents?(?: only| parking| bay)|red route|controlled parking|bus lane|no parking|double yellows?|yellow lines?|clamp(?:ed|ing)?|tow(?:ed)? away|shut the gate|close the gate|keep (?:the )?gate (?:shut|closed)|gate sticks|code (?:is|changed|on)|not allowed)\b/i
// ...unless the same sentence says it no longer applies (Sonnet 3): "no dog any more", "ladder not needed".
const NOT_NOW = /\b(?:no longer|not needed|no more|any ?more|gone|died|passed away|don'?t need|doesn'?t need|not there|never|not locked|unlocked)\b|\bno (?:dogs?|alarm|keys?)\b|\bno \w+ needed\b|\b(?:gate|door) (?:is )?(?:always )?(?:open|unlocked)\b/i

// Sentences, then their clauses: "No ladder needed, dog in garden" is two, so the first's "no" never hides the dog
// (Sonnet's notes review 1). "St. Johns", "No. 4" and "10.30" are not ends of sentences.
function clauses(text: string, own = false): string[] {
  const lines = text.split(/\n/).map((l) => (own ? l.replace(VOICE_PREFIX, '') : l)).filter((l) => !(own && SYSTEM_LINE.test(l)))
  return lines.flatMap((l) => l.split(/(?<![A-Z][a-z]?|\bNo|\bSt|\d)[.!?;]\s+|,\s+|\s+[—–-]\s+|\s+but\s+/i)).map((x) => x.trim()).filter(Boolean)
}
const sentences = (text: string, own = false): string[] => clauses(text, own)

// Roundly's guess for a note's words, and what in them made it a warning ("it says dog").
export function guessKind(text: string | null | undefined, own = false): { kind: NoteKind; word: string | null } {
  for (const s of sentences(String(text ?? ''), own)) {
    const m = WARN.exec(s)
    if (m && !NOT_NOW.test(s)) return { kind: 'warn', word: m[1]!.toLowerCase() }
  }
  return { kind: 'note', word: null }
}

// A note's kind, chosen or guessed. A parking note with hours Roundly can read is a Warning by its guess (in its hours).
export function kindOf(field: ChosenField, text: string, choices: Choices): { kind: NoteKind; chosen: boolean; word: string | null } {
  const c = choices[field]
  if (c && (c.kind === 'warn' || c.kind === 'note') && c.for === wordsKey(text)) return { kind: c.kind, chosen: true, word: null }
  if (field === 'parking_restrictions') {
    // Hours Roundly can read: a Warning in those hours. None it can read: a Warning unless it says parking is fine there
    // ("Driveway available"), to be safe (Sonnet's notes review 6).
    const rules = parkingRules(text)
    if (rules.easy) return { kind: 'note', chosen: false, word: null }
    if (rules.ranges.length || rules.permit) return { kind: 'warn', chosen: false, word: null }
    if (!/\b(?:not allowed|no parking|restrict|permit|resident)/i.test(text) && /\b(?:available|allowed|free|driveway|drive|plenty|space|fine|ok|okay|no restrictions?)\b/i.test(text)) return { kind: 'note', chosen: false, word: null }
    return { kind: 'warn', chosen: false, word: 'parking' }
  }
  const g = guessKind(text)
  return { kind: g.kind, chosen: false, word: g.word }
}

export interface DoorNote { field: ChosenField; text: string }
export interface StopNotes {
  addressWarn: string | null
  notes: DoorNote[]
  choices: Choices
  visitNote: string | null
  lastTime: string | null
  // The visit's time as shown (visibleTime), and how long it takes, for parking; its day, for days of the week.
  time: string | null
  date?: string | null
  lengthMinutes?: number | null
}

// One line of a stop's notes, with whether it warns on THIS visit.
export interface NoteLine { text: string; warn: boolean; field?: ChosenField; why?: string }

export function stopLines(s: StopNotes): NoteLine[] {
  const lines: NoteLine[] = []
  if (s.addressWarn) lines.push({ text: s.addressWarn, warn: true })
  for (const n of s.notes) {
    const k = kindOf(n.field, n.text, s.choices)
    if (n.field === 'parking_restrictions') {
      const rules = parkingRules(n.text)
      const hours = rules.ranges.length > 0
      // Red in its hours (on that day of the week, start to finish: parking-rules.ts); with no time, or hours Roundly can't
      // read, red to be safe (Sonnet and GPT-6.1). A time Roundly can't read ("10am", "morning") counts as no time (Sonnet's
      // notes review 8).
      const time = s.time && /^\s*\d{1,2}:\d{2}/.test(s.time) ? s.time : null
      // ...and a restriction that isn't about the hours ("permit only all day") warns at any time (GPT-6.1's review 11).
      const allDay = rules.permit || /\b(?:red route|double yellows?|all day|24 ?h(?:ours|rs)?)\b/i.test(n.text)
      const warnNow = k.kind === 'warn' && (!hours || !time || allDay || rangesHit(rules, s.date ?? null, time, s.lengthMinutes ?? null).length > 0)
      const why = hours && k.kind === 'warn' ? (!time ? 'set a time to check it' : warnNow ? "you're there inside it" : "you're there outside it") : undefined
      lines.push({ text: n.text, warn: warnNow, field: n.field, ...(why ? { why } : {}) })
    } else lines.push({ text: n.text, warn: k.kind === 'warn', field: n.field })
  }
  if (s.visitNote?.trim()) for (const line of s.visitNote.split(/\n/).map((l) => l.trim()).filter(Boolean)) lines.push({ text: line, warn: guessKind(line, true).kind === 'warn' })
  if (s.lastTime?.trim()) lines.push({ text: `From last time: ${s.lastTime.trim()}`, warn: guessKind(s.lastTime, true).kind === 'warn' })
  return lines
}

// The stop's mark: red "!" when anything warns on this visit, a grey note when there are only notes, else nothing.
export function stopMark(s: StopNotes): 'warn' | 'note' | null {
  const lines = stopLines(s)
  if (!lines.length) return null
  return lines.some((l) => l.warn) ? 'warn' : 'note'
}
